import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Play, Loader2, ArrowLeft } from "lucide-react";
import { api, imgUrl } from "../lib/api";
import { Navbar } from "../components/Navbar";
import { EpisodeCard } from "../components/EpisodeCard";

export default function SeriesPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [series, setSeries] = useState(null);
  const [loading, setLoading] = useState(true);
  const [season, setSeason] = useState(1);

  useEffect(() => {
    setLoading(true);
    api.get(`/series/${id}`).then((r) => {
      setSeries(r.data);
      setSeason(r.data.seasons?.[0] || 1);
    }).finally(() => setLoading(false));
  }, [id]);

  if (loading)
    return <div className="min-h-screen flex items-center justify-center bg-background"><Loader2 className="animate-spin text-blood" size={40} /></div>;

  if (!series) return <div className="min-h-screen grain"><Navbar /><p className="pt-32 text-center text-white/50">Series not found.</p></div>;

  const seasonEps = series.episodes.filter((e) => e.season_number === season);
  const play = () => { if (seasonEps[0]) navigate(`/watch/${seasonEps[0].id}`); };

  return (
    <div className="min-h-screen grain pb-24 md:pb-12">
      <Navbar />

      <section className="relative min-h-[70vh] flex items-end" data-testid="series-banner">
        <div className="absolute inset-0">
          {series.banner_url || series.poster_url ? (
            <img src={imgUrl(series.banner_url || series.poster_url)} alt={series.title} className="w-full h-full object-cover" />
          ) : (<div className="w-full h-full bg-gradient-to-br from-[#1a0005] to-black" />)}
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/70 to-black/30" />
        </div>
        <button onClick={() => navigate(-1)} data-testid="series-back-btn" className="absolute top-20 left-4 sm:left-8 z-20 flex items-center gap-1.5 text-white/70 hover:text-white bg-black/40 backdrop-blur px-3 py-1.5 rounded-full text-sm">
          <ArrowLeft size={16} /> Back
        </button>
        <div className="relative z-10 max-w-[1600px] w-full mx-auto px-4 sm:px-8 pb-12 pt-28 flex flex-col md:flex-row gap-8 items-end">
          {series.poster_url && (
            <img src={imgUrl(series.poster_url)} alt={series.title} className="hidden md:block w-44 aspect-[2/3] object-cover rounded-xl border border-white/10 shadow-2xl" />
          )}
          <div className="flex-1">
            <p className="text-xs tracking-[0.3em] uppercase font-bold text-blood mb-2 glow-red">DAKID Original</p>
            <h1 data-testid="series-title" className="font-display font-black text-4xl sm:text-6xl tracking-tighter text-white leading-[0.95]">{series.title}</h1>
            <p className="mt-3 text-sm text-white/60 font-semibold">
              {series.genres?.join("  ·  ")}{series.year ? `  ·  ${series.year}` : ""}
              {series.seasons?.length ? `  ·  ${series.seasons.length} Season${series.seasons.length > 1 ? "s" : ""}` : ""}
            </p>
            <p className="mt-4 text-white/75 max-w-2xl leading-relaxed">{series.description}</p>
            <button onClick={play} data-testid="series-play-btn" disabled={!seasonEps.length} className="mt-6 flex items-center gap-2 px-8 py-3.5 rounded-full bg-blood text-white font-bold shadow-[0_0_18px_rgba(220,20,60,0.45)] hover:bg-blood-hover disabled:opacity-40 transition-all">
              <Play size={20} className="fill-white" /> Play
            </button>
          </div>
        </div>
      </section>

      <div className="max-w-[1600px] mx-auto px-4 sm:px-8 mt-8">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
          <h2 className="text-2xl font-display font-bold text-white">Episodes</h2>
          {series.seasons?.length > 1 && (
            <select
              value={season}
              onChange={(e) => setSeason(Number(e.target.value))}
              data-testid="season-select"
              className="bg-[#111111] border border-white/10 rounded-full px-5 py-2.5 text-sm font-semibold text-white outline-none focus:ring-1 focus:ring-blood"
            >
              {series.seasons.map((s) => <option key={s} value={s}>Season {s}</option>)}
            </select>
          )}
        </div>

        {seasonEps.length === 0 ? (
          <p className="text-white/40 py-10">No episodes in this season yet.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="episode-list">
            {seasonEps.map((e, i) => <EpisodeCard key={e.id} episode={e} index={i} />)}
          </div>
        )}
      </div>
    </div>
  );
}
